import { useState } from 'react';
import { 
    Trash2, 
    Pen, 
    Copy, 
    Plus, 
    Search, 
    ChevronDown, 
    ChevronUp,
    Clock,
    Trophy,
    Target,
    MessageSquare,
    Lightbulb,
    Star,
    Minus
} from 'lucide-react';

type DiaryEntryProps = {
    sessionStatus: string;
    entry: any;
    index: number;
    remove: (id: string) => void;
    setEditing: (entry: any) => void;
    addRep: (entry: any) => void;
    duplicate: (entry: any) => void;
    openReadOnly?: (entry: any) => void;
};

const DiaryEntry: React.FC<DiaryEntryProps> = ({ sessionStatus, entry, index, remove, setEditing, addRep, duplicate, openReadOnly }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const getTypeColor = (type: string) => {
        const colors: Record<string, string> = {
            'custom-select': 'from-purple-500 to-purple-600',
            'Custom': 'from-purple-500 to-purple-600',
            'new-skill': 'from-blue-500 to-blue-600',
            'section': 'from-green-500 to-green-600',
            'default': 'from-gray-500 to-gray-600'
        };
        return colors[type] ?? colors['default'];
    };

    const getTypeIcon = (type: string) => {
        switch(type) {
            case 'new-skill': return <Star className="w-4 h-4" />;
            case 'section': return <Target className="w-4 h-4" />;
            default: return <Trophy className="w-4 h-4" />;
        }
    };

    const displayType = entry.objective.type !== 'custom-select' ? entry.objective.type : 'Custom';

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl mb-4">
            {/* Header */}
            <div className={`bg-gradient-to-r ${getTypeColor(displayType)} p-4 text-white relative`}>
                <div className="absolute inset-0 bg-white opacity-10"></div>
                <div className="relative flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        {getTypeIcon(entry.objective.type)}
                        <div>
                            <h3 className="font-bold text-lg">
                                {index}, {displayType}
                            </h3>
                            {entry.repetitions > 1 && (
                                <span className="text-sm bg-white bg-opacity-20 px-2 py-1 rounded-full text-gray-800">
                                    {entry.repetitions}x reps
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                        {sessionStatus === 'active' ? (
                            <>
                                <button
                                    onClick={() => setEditing(entry)}
                                    className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                    title="Edit"
                                >
                                    <Pen className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                                </button>

                            {entry.objective.type !== 'new-skill' && (
                                <>
                                    <button
                                        onClick={() => duplicate(entry)}
                                        className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                        title="Duplicate"
                                    >
                                        <Copy className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                                    </button>

                                    {entry.repetitions > 1 && (
                                        <button
                                            onClick={() => addRep(entry)}
                                            className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                            title="Add Rep"
                                        >
                                            <Plus className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                                        </button>
                                    )}
                                </>
                            )}

                            <button
                                onClick={() => remove(entry.id)}
                                className="w-8 h-8 bg-red-500 bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                title="Delete"
                            >
                                <Trash2 className="w-4 h-4 text-white" />
                            </button>
                        </>
                        ) : (
                            <button
                                onClick={() => setEditing(entry)}
                                className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                title="View Details"
                            >
                                <Search className="w-4 h-4 text-gray-800 hover:text-gray-900" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="p-6">
                {/* Objective Name */}
                <div className="mb-4">
                    <h4 className="text-xl font-semibold text-gray-800 leading-tight">
                        {isExpanded ? entry.objective.name : (entry.objective.name.length > 55 ? entry.objective.name.slice(0, 55) + '...' : entry.objective.name)}
                        {entry.objective.type === 'section' && (
                            <span className="text-sm text-gray-500 block mt-1">
                                Skills {entry.objective.parent_first_skill} to {entry.objective.parent_last_skill}
                            </span>
                        )}
                    </h4>
                </div>

                {/* Quick Stats */}
                {!isExpanded && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-sm text-gray-500">Difficulty</div>
                            <div className="text-lg font-bold text-gray-800">
                                {entry.total_difficulty}{entry.repetitions > 1 ? ' each' : ''}
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-sm text-gray-500">Skills Completed</div>
                            <div className="text-lg font-bold text-gray-800">
                                {entry.total_completed_skills}
                            </div>
                        </div>
                    </div>
                )}

                {/* Expanded Details */}
                <div className={`transition-all duration-300 overflow-hidden ${
                    isExpanded ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
                }`}>
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        {/* Performance Metrics */}
                        <div className="grid grid-cols-2 gap-4">
                            {entry.total_tof > 0 && (
                                <div className="bg-blue-50 rounded-lg p-4 flex items-center space-x-3">
                                    <Clock className="w-5 h-5 text-blue-500" />
                                    <div>
                                        <div className="text-sm text-blue-600 font-medium">
                                            {entry.time_method === 'stopwatch' ? 'Stopwatch Time' : 'Time of Flight'}
                                        </div>
                                        <div className="text-lg font-bold text-blue-800">
                                            {entry.total_tof}s
                                        </div>
                                    </div>
                                </div>
                            )}

                            {entry.total_hd > 0 && (
                                <div className="bg-green-50 rounded-lg p-4 flex items-center space-x-3">
                                    <Trophy className="w-5 h-5 text-green-500" />
                                    <div>
                                        <div className="text-sm text-green-600 font-medium">HD Score</div>
                                        <div className="text-lg font-bold text-green-800">
                                            {entry.total_hd}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {entry.execution > 0 && (
                                <div className="bg-purple-50 rounded-lg p-4 flex items-center space-x-3">
                                    <Star className="w-5 h-5 text-purple-500" />
                                    <div>
                                        <div className="text-sm text-purple-600 font-medium">Execution</div>
                                        <div className="text-lg font-bold text-purple-800">
                                            {entry.execution}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {entry.landing_penalty != null && entry.landing_penalty !== 999 && (
                                <div className="bg-red-50 rounded-lg p-4 flex items-center space-x-3">
                                    <Minus className="w-5 h-5 text-red-500" />
                                    <div>
                                        <div className="text-sm text-red-600 font-medium">Landing Deduction</div>
                                        <div className="text-lg font-bold text-red-800">
                                            {entry.landing_penalty}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-sm text-gray-500 font-medium">Completed Skills</div>
                                <div className="text-xl font-bold text-gray-800">
                                    {entry.total_completed_skills  + (entry.repetitions > 1 ? ' each' : '')}
                                </div>
                            </div>
                            {entry.repetitions > 1 && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="text-sm text-gray-500 font-medium">Total Skills</div>
                                    <div className="text-xl font-bold text-gray-800">
                                        {(entry.total_completed_skills * entry.repetitions).toFixed(0)}
                                    </div>
                                </div>
                            )}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-sm text-gray-500 font-medium">Difficulty</div>
                                <div className="text-xl font-bold text-gray-800">
                                    {entry.total_difficulty}{entry.repetitions > 1 ? ' each' : ''}
                                </div>
                            </div>
                            {entry.repetitions > 1 && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="text-sm text-gray-500 font-medium">Total Difficulty</div>
                                    <div className="text-xl font-bold text-gray-800">
                                        {(entry.total_difficulty * entry.repetitions).toFixed(1)}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Feedback Section */}
                        {entry.feedback && entry.feedback.trim() && (
                            <div className="bg-amber-50 rounded-lg p-4">
                                <div className="flex items-center space-x-2 mb-2">
                                    <MessageSquare className="w-5 h-5 text-amber-600" />
                                    <span className="font-semibold text-amber-800 text-sm">Feedback</span>
                                </div>
                                <p className="text-base text-amber-700 leading-relaxed">
                                    {entry.feedback}
                                </p>
                            </div>
                        )}

                        {/* Self Reflection Section */}
                        {entry.self_reflection && entry.self_reflection.trim() && (
                            <div className="bg-indigo-50 rounded-lg p-4">
                                <div className="flex items-center space-x-2 mb-2">
                                    <Lightbulb className="w-5 h-5 text-indigo-600" />
                                    <span className="font-semibold text-indigo-800 text-sm">Self Reflection</span>
                                </div>
                                <p className="text-base text-indigo-700 leading-relaxed">
                                    {entry.self_reflection}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Toggle Button */}
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="w-full mt-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 font-medium text-gray-700"
                >
                    <span className="text-base">{isExpanded ? 'Show Less' : 'Show More'}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
};

export default DiaryEntry;